import dayjs from 'dayjs'
import * as echarts from 'echarts'
import { Flex } from '@mantine/core'
import { observer } from 'mobx-react-lite'
import { useEffect, useMemo, useRef, FC } from 'react'
import { useStore } from '@/store/index'
import { getTextWidth, deepClone } from '@/utils/tools'
import useRateChange from '@/hooks/useRateChange'
import BaseEmpty from '@/components/BaseEmpty'
import type { ECharts, EChartsOption } from 'echarts'
import useThemeVar from '@/hooks/useThemeStr'
import { EnumCoinLineTimeTab, colorMap } from '@/constants/index'
import { ChartTrendBoxStyled } from './style'
import { CoinChartsStyled } from '@/blocks/Price/PriceChart/style'
import { useAdapter } from '@/components/Adapter'

interface PlatePriceItem {
  category_id: string
  en_name: string
  list: PlatePriceChildItem[]
}

interface PlatePriceChildItem {
  id: number
  category_id: string
  time_span: string
  crawl_ts: string
  volatility: string
  en_name: string
  plate_markets: number | string
}

type TrendChartProps = {
  data: PlatePriceItem[]
  lineTab: EnumCoinLineTimeTab[keyof EnumCoinLineTimeTab]
}

interface dealChartSeriesProps {
  series: any[]
  grid: EChartsOption['grid']
  nameData: string[]
  xData: string[]
  maxValue: number
  minValue: number
  left: number
}

/**
 * @description: 时间轴显示规则
 */
export const xAxisLabelTime = (xTime: number, type: EnumCoinLineTimeTab[keyof EnumCoinLineTimeTab]) => {
  if (type === EnumCoinLineTimeTab['ONE_DAY']) return dayjs(xTime * 1000).format('MM-DD HH:mm')
  if (type === EnumCoinLineTimeTab['SEVEN_DAY']) return dayjs(xTime * 1000).format('MM-DD HH:mm')
  if (type === EnumCoinLineTimeTab['ONE_MONTH']) return dayjs(xTime * 1000).format('MM-DD HH:mm')
  if (type === EnumCoinLineTimeTab['ONE_YEAR']) return dayjs(xTime * 1000).format('YYYY-MM-DD')
  return dayjs(xTime * 1000).format('YYYY-MM-DD')
}

const TrendChart: FC<TrendChartProps> = ({ data, lineTab }) => {
  const { isDark } = useStore().globalStore
  const { isH5 } = useAdapter()

  const trendChartRef = useRef<HTMLDivElement>(null) // 用于绑定 DOM 容器
  const trendEchartsInstance = useRef<(echarts.EChartsType & any) | null>(null) // 保存 ECharts 实例

  const { formatRateChangePrice } = useRateChange()

  const { themeVar } = useThemeVar()

  /**
   * @description: 处理chart数据
   * @param {Record} data chart数组数据
   * @param {*} any
   * @param {number} isSliceNumber
   * @return {*}
   */
  const dealChartSeries = (data: Record<string, any>[], isSliceNumber?: number): dealChartSeriesProps => {
    // 最大值 | 最小值 中间变量
    let maxValue = 0,
      minValue = 0
    // 配置当前选中数据
    const dealShowData = isSliceNumber ? data?.slice(0, isSliceNumber) : data
    const nameData = [...['All'], ...data.map(item => item?.en_name ?? '')]
    const visibleLineLists = dealShowData?.map(line => line.list).flat(1)

    const timeLineData = Array.from(new Set(visibleLineLists?.map(item => item.crawl_ts) ?? []))
    const timeSortAll = timeLineData.sort((a: any, b: any) => a - b)

    // 默认配置当前选中数据
    const defaultData = isSliceNumber
      ? (deepClone(data)?.map((item, index) => {
          if (index < isSliceNumber + 1) {
            return item
          }
          return {
            ...item,
            list: [],
          }
        }) ?? [])
      : data

    //  数组绑定当前时间戳为唯一key
    const newVisibleLine = defaultData?.map(item => {
      const paddedArray: any[] = []
      for (let ind = 0; ind < timeSortAll.length; ind++) {
        const tim = timeSortAll[ind]
        const itemCt = item?.list.find((ite: any) => ite.crawl_ts === tim)

        if (itemCt) {
          paddedArray[ind] = itemCt
        } else {
          paddedArray[ind] = {
            crawl_ts: tim,
            plate_markets: null,
          } as any
        }
      }

      return {
        ...item,
        list: paddedArray,
      }
    })

    const currentData = [{ category_id: 'All', en_name: 'All', list: [] }, ...newVisibleLine]

    const series = currentData?.map((item: any, index: number) => {
      return {
        type: 'line',
        data: item?.list?.map((listItem: any) => {
          // 最大值过滤 ｜ 计算grid
          if (Number(listItem?.plate_markets) > maxValue) {
            maxValue = Number(listItem?.plate_markets)
          }
          // 最小值过滤 ｜ 计算grid
          if (Number(listItem?.plate_markets) < minValue) {
            minValue = Number(listItem?.plate_markets)
          }

          return listItem?.plate_markets
        }),
        name: item?.en_name ?? '',
        lineStyle: {
          color: colorMap[index],
        },
        color: colorMap[index],
        showSymbol: false,
        symbolSize: 12, // 数据点大小
        emphasis: {
          focus: 'series',
        },
      }
    })

    const max = getTextWidth(
      formatRateChangePrice(String(maxValue), { isThousands: true }) + '',
      12,
      'Gate_Sans,Arial,sans-serif'
    )
    const min = getTextWidth(
      formatRateChangePrice(String(minValue), { isThousands: true }) + '',
      12,
      'Gate_Sans,Arial,sans-serif'
    )

    const left = (max > min ? max : min) + 30

    const grid = {
      right: '36px', // 右边距
      left: left ? left + 'px' : 'auto',
      bottom: '94px',
      top: '2%',
    }

    return {
      maxValue,
      minValue,
      series,
      grid,
      left,
      nameData,
      xData: timeSortAll,
    }
  }

  /**
   * @description:chart 缓存数据
   * @param {*} useMemo
   * @return {*}
   */
  const lineData = useMemo(() => {
    const filterReg = /[-(){} ]/g

    const lineDealData = dealChartSeries(data, 6)

    const filterNameData = lineDealData?.nameData.map(key => key.replaceAll(filterReg, ''))

    return {
      xData: lineDealData?.xData,
      filterNameData,
      nameData: lineDealData?.nameData,
      filterReg,
      grid: lineDealData?.grid,
      left: lineDealData?.left,
      series: lineDealData?.series,
      maxValue: lineDealData?.maxValue,
      minValue: lineDealData?.minValue,
    }
  }, [data])

  useEffect(() => {
    if (!trendChartRef.current) return
    trendEchartsInstance.current = echarts.init(trendChartRef.current) as ECharts | undefined

    const options = {
      legend: {
        type: 'scroll',
        bottom: 0,
        orient: 'horizontal',
        itemGap: 8,
        itemWidth: 0,
        itemHeight: 0,
        selectedMode: 'multiple',
        pageTextStyle: {
          color: isDark ? '#FAFAFA' : '#262933',
        },
        pageIcons: {
          horizontal: [pageIconGather.left, pageIconGather.right],
        },
        pageIconColor: themeVar('--color-icon-1'),
        pageIconInactiveColor: themeVar('--color-icon-7'),
        pageIconSize: 12,
        formatter: (name: string) => {
          if (trendEchartsInstance.current) {
            const selected = trendEchartsInstance.current.getOption().legend[0].selected[name]
            return `{${name?.replaceAll(lineData.filterReg, '') + `${selected ? '' : '_selected'}`}|}{name|${name}}`
          }
        },
        textStyle: {
          rich: {
            name: {
              color: isDark ? '#FAFAFA' : '#262933',
              fontSize: 12,
              fontWeight: 500,
              padding: [12, 24, 12, 8],
            },
            ...lineData.filterNameData?.reduce((obj: any, key, index) => {
              obj[key + '_selected'] = {
                width: 12,
                height: 12,
                backgroundColor: {
                  image: createNoSelectedSVG({ fill: colorMap[index] }),
                },
              }
              return obj
            }, {}),
            ...lineData.filterNameData?.reduce((obj: any, key, index) => {
              obj[key] = {
                width: 12,
                height: 12,
                backgroundColor: {
                  image: createSelectedSVG({ fill: colorMap[index] }),
                },
              }
              return obj
            }, {}),
          },
        },
        selected: lineData.nameData?.reduce((obj: any, key, index) => {
          if (index < 7 && index > 0) {
            obj[key] = true
          } else {
            obj[key] = false
          }
          return obj
        }, {}),
      },
      grid: {
        ...lineData?.grid,
        bottom: isH5 ? '94px' : '110px',
        right: '36px',
        left: isH5 ? lineData?.left - 10 : lineData?.left,
      },
      dataZoom: [
        {
          start: 0,
          end: 100,
          handleSize: isH5 ? '16px' : '24px',
          height: isH5 ? 16 : 24,
          backgroundColor: 'transparent',
          fillerColor: isDark ? 'rgba(38, 111, 255, 0.1)' : 'rgba(35, 84, 230, 0.1)',
          borderColor: isDark ? '#262933' : '#E6E9F2',
          dataBackground: {
            lineStyle: {
              color: isDark ? '#474F66' : '#E6E9F2',
              width: 1,
            },
            areaStyle: {
              color: isDark ? '#262933' : '#F8FAFD',
              opacity: 1,
            },
          },
          showDetail: false,
          bottom: '38px',
        },
      ],
      tooltip: {
        trigger: 'axis',
        confine: true,
        extraCssText: 'z-index: 20',
        padding: [12, 12, 12, 12],
        borderWidth: 0, // 提示框边框宽度
        backgroundColor: themeVar('--color-cmpt-6'), // 提示框背景色，深色模式和浅色模式
        // extraCssText: 'box-shadow: 0px 4px 24px 0px #04091914', // 提示框额外的CSS样式
        textStyle: {
          color: themeVar('--color-text-6'),
          fontSize: 12,
          fontWeight: 500,
          lineHeight: 1.5,
        },
        formatter: function (params: any) {
          // 构建排序数组
          const sortParams = [...params].sort((a, b) => {
            return b?.data - a?.data
          })

          let relVal = `<strong style="font-weight:500;font-size:16px;font-family:Gate_Sans">${xAxisLabelTime(params[0]?.axisValue, lineTab)}</strong>`
          for (let i = 0, l = sortParams.length; i < l; i++) {
            const item = sortParams[i]
            const index = lineData.nameData.findIndex(key => key === item?.seriesName)
            relVal += `<div style="display: flex; gap: 8px; align-items: center;">
              <span style="display:inline-block;margin-right:4px;border-radius:2px;width:8px;height:8px;background-color:${
                colorMap[index]
              };"></span>
              <span style="font-size: 14px;font-weight: 500;">
              ${item.seriesName}: 
              </span>
              <strong style="font-size: 14px;font-weight: 700;">${formatRateChangePrice(item?.data, {
                isUnit: true,
                isThousands: true,
              })}</strong>
            </div>`
          }
          return `<div style="display: flex;flex-direction: column;gap: 8px;">
            ${relVal}
          </div>`
        },
      },
      xAxis: {
        data: lineData.xData,
        boundaryGap: false,
        axisLine: {
          show: false,
          lineStyle: {
            color: themeVar('--color-divider-1'),
            width: 1,
          },
        },
        axisTick: {
          length: 8,
          alignWithLabel: true,
          lineStyle: {
            color: themeVar('--color-divider-1'),
            width: 0, // 隐藏刻度线
          },
          interval: 4,
          minInterval: 3600 * 24 * 1000,
        },
        axisLabel: {
          fontSize: isH5 ? 10 : 12,
          color: themeVar('--color-text-3'),
          fontWeight: 500,
          margin: 16,
          fontFamily: 'Gate_Sans',
          formatter: function (value: any, index: number) {
            if (index !== 0) {
              return xAxisLabelTime(value, lineTab)
            }
          },
        },
      },
      yAxis: {
        type: 'value',
        boundaryGap: [0, '10%'],
        splitNumber: 6,
        position: 'left',
        splitLine: {
          lineStyle: {
            type: 'dashed', // 设置分隔线为虚线
            color: themeVar('--color-divider-2'),
            width: 1,
            opacity: 0.7,
          },
        },
        axisLine: {
          show: true,
          lineStyle: {
            color: isDark ? '#2A2F40' : '#E6E9F2',
            width: 1,
          },
        },
        axisLabel: {
          formatter: function (value: any) {
            if (value === 0) return 0
            return `${formatRateChangePrice(value, { isUnit: true, isThousands: true })}`
          },
          textStyle: {
            color: themeVar('--color-text-3'),
            fontSize: isH5 ? 10 : 12,
            fontWeight: 500,
            fontFamily: 'Gate_Sans',
          },
        },
      },
      series: lineData?.series,
      graphic: {
        type: 'image',
        z: -10,
        left: 'center',
        top: isH5 ? '30%' : '30%',
        style: {
          image: '/price/public/images/icon-gt-logo.png',
          width: isH5 ? 90 : 180,
          height: isH5 ? 21 : 42,
          opacity: 0.8,
        },
      },
    }

    trendEchartsInstance.current.setOption(options)

    // 监听图例选中事件  params: echarts.ECActionEvent
    trendEchartsInstance.current.on('legendselectchanged', (params: any) => {
      // 获取所有图例的选中状态
      const currentSelectObj: any = {}

      if (params?.name === 'All') {
        if (params['selected']?.All) {
          const currentAllChartData = dealChartSeries(data)
          Object.keys(params['selected']).forEach((item: string) => (currentSelectObj[item] = true))

          const configAllChartOptions = {
            series: currentAllChartData?.series,
            grid: currentAllChartData?.grid,
            legend: {
              ...options.legend,
              selected: currentSelectObj,
            },
            xAxis: {
              data: currentAllChartData?.xData,
            },
          }
          trendEchartsInstance.current.setOption(configAllChartOptions)
        }

        if (!params['selected']?.All) {
          const currentOneChartData = dealChartSeries(data, 1)
          Object.keys(params['selected']).forEach((item: string, index: number) => {
            if (index === 1) {
              currentSelectObj[item] = true
            } else {
              currentSelectObj[item] = false
            }
          })

          const configOneChartOptions = {
            series: currentOneChartData?.series,
            grid: currentOneChartData?.grid,
            legend: {
              ...options.legend,
              selected: currentSelectObj,
            },
            xAxis: {
              data: currentOneChartData?.xData,
            },
          }
          trendEchartsInstance.current.setOption(configOneChartOptions)
        }
        return
      }

      const selected = params.selected

      // 根据选中的系列，动态调整 x 轴的数据范围
      const visibleLine = data?.filter(lineItem => selected[lineItem.en_name as string])

      // 如果有选中数据，则获取最小和最大时间戳，设置为 X 轴范围
      if (visibleLine.length > 0) {
        const currentOneChartData = dealChartSeries(visibleLine)

        // 更新 X 轴
        trendEchartsInstance.current.setOption({
          xAxis: {
            data: currentOneChartData?.xData,
          },
          grid: currentOneChartData?.grid,
          series: currentOneChartData?.series?.map((item, index) => {
            const currentColor =
              options?.series?.find(curItem => curItem?.name === item?.name)?.color ?? colorMap[index]
            return {
              ...item,
              lineStyle: {
                color: currentColor,
              },
              color: currentColor,
            }
          }),
        })
      }
    })

    const resizeHandler = (e: any) => {
      if (e?.[0].contentRect?.width !== trendEchartsInstance.current.getWidth()) {
        trendEchartsInstance.current.resize()
      }
    }

    const observer = new ResizeObserver(resizeHandler)

    if (trendChartRef.current) {
      observer.observe(trendChartRef.current)
    }

    window.addEventListener('resize', () => trendEchartsInstance.current.resize())

    return () => {
      window.removeEventListener('resize', () => trendEchartsInstance.current.resize())
      observer.disconnect()
    }
  }, [formatRateChangePrice, isDark, isH5, lineData, data, lineTab, themeVar])

  return (
    <CoinChartsStyled>
      {data?.length ? (
        <ChartTrendBoxStyled>
          <div
            key={`${isDark}-${JSON.stringify(lineData?.nameData)}-line-graph`}
            className="price-coin-trend-chart"
            ref={trendChartRef}
          />
        </ChartTrendBoxStyled>
      ) : (
        <Flex h={isH5 ? 285 : 410} align="center">
          <BaseEmpty size="md" />
        </Flex>
      )}
    </CoinChartsStyled>
  )
}

export const createSelectedSVG = ({ fill }: { fill: string }) => {
  const svg = `<svg width="14" height="14" viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg">
  <path d="M0 1.75C0 0.783502 0.783502 0 1.75 0H12.25C13.2165 0 14 0.783502 14 1.75V12.25C14 13.2165 13.2165 14 12.25 14H1.75C0.783502 14 0 13.2165 0 12.25V1.75Z" fill="${fill}"/>
  <path d="M3.18262 6.66523L5.90026 9.55273L10.8826 4.74023" stroke="white" stroke-width="1.3125" stroke-linecap="round" stroke-linejoin="round"/>
  </svg>
  `
  return `data:image/svg+xml;base64,${btoa(svg)}`
}

export const createNoSelectedSVG = ({ fill }: { fill: string }) => {
  const svg = `<svg width="14" height="14" viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg">
  <rect x="0.4375" y="0.4375" width="13.125" height="13.125" rx="1.3125" stroke="${fill}" stroke-width="0.875"/>
  </svg>
  `
  return `data:image/svg+xml;base64,${btoa(svg)}`
}

export const pageIconGather = {
  left: 'path://M3.776 6.409a.5.5 0 0 1 0-.816l3.435-2.434A.5.5 0 0 1 8 3.567v4.867a.5.5 0 0 1-.789.408L3.776 6.409Z', // 左箭头 SVG 路径
  right: 'path://M8.224 5.591a.5.5 0 0 1 0 .816L4.789 8.841A.5.5 0 0 1 4 8.433V3.566a.5.5 0 0 1 .789-.408l3.435 2.433Z', // 右箭头 SVG 路径
}

export default observer(TrendChart)
